import get from 'lodash/get'
import type { ReactNode } from 'react'
import React from 'react'
import type { FileRejection } from 'react-dropzone'

import {
  InputUI,
  Loader,
  Loading,
  Subsection,
  Toasts,
  Upload,
  useMediaQueryContext,
} from '@fourthwall/components'
import { UploadIcon } from '@fourthwall/icons'
import { arrayMove } from '@fourthwall/utils/dist/array'
import { useFormikContext } from '@fourthwall/utils/dist/formik'

import { getRejectionErrors } from '../../../../../../../utils/dropzone'
import { PostGallery } from '../../../../components/PostGallery/PostGallery'
import { RemovableUpload } from '../../../../components/RemovableUpload/RemovableUpload'
import { UploadedImage } from '../../../../components/UploadedImage/UploadedImage'
import { MAX_IMAGE_POST_IMAGES } from '../../../../consts'
import type { FormValues, Image } from '../../types'
import * as S from './styled'

type Props = {
  imagesFieldName: string
  subsectionTitle?: string
  isUploading?: boolean
  isLoading?: boolean
  onImagesUpload?: (files: File[]) => void
}

export const ImagesFields = ({
  imagesFieldName,
  subsectionTitle,
  isUploading,
  isLoading,
  onImagesUpload,
}: Props) => {
  const media = useMediaQueryContext()
  const { values, errors, setFieldValue } = useFormikContext<FormValues>()

  const handleImageUpload = async (files?: File[]) => {
    if (files && files.length > MAX_IMAGE_POST_IMAGES) {
      return Toasts.notify(`You can upload up to ${MAX_IMAGE_POST_IMAGES} images within a post.`, {
        type: 'error',
      })
    }

    if (files && onImagesUpload) {
      onImagesUpload(files)
    }
  }

  const handleImageRemove = (index: number) => {
    if (values.images) {
      setFieldValue(
        imagesFieldName,
        values.images.filter((image, imageIndex) => imageIndex !== index),
      )
    }
  }

  const handleImageChange = (newIndex: number, oldIndex: number) => {
    if (values.images) {
      setFieldValue(imagesFieldName, arrayMove(values.images, newIndex, oldIndex))
    }
  }

  const handleDropRejection = (rejections: FileRejection[]) => {
    getRejectionErrors(rejections).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const renderImages = () => {
    const images = values[imagesFieldName as keyof FormValues] as Image[]

    if (!images.length) {
      return null
    }

    if (images.length === 1) {
      return (
        <RemovableUpload
          onRemove={() => {
            handleImageRemove(0)
          }}
        >
          <UploadedImage image={images[0].image} objectFit="contain" />
        </RemovableUpload>
      )
    }

    return (
      <PostGallery
        images={images}
        onImageRemove={handleImageRemove}
        onImageSort={handleImageChange}
      />
    )
  }

  const renderOverlay = () => {
    if (isUploading) {
      return (
        <>
          <S.Overlay />
          <S.MessageContainer>
            <Loader size="large" appearance="primary" />
            <S.Message>Uploading</S.Message>
          </S.MessageContainer>
        </>
      )
    }
  }

  const renderUpload = () => {
    const images = values[imagesFieldName as keyof FormValues] as Image[]

    if (images.length === MAX_IMAGE_POST_IMAGES) {
      return null
    }

    const hadUploadedImage = images.length > 0

    const height = hadUploadedImage ? 80 : 120
    let title: ReactNode = 'Browse'

    if (media.tablet) {
      title = hadUploadedImage ? (
        <span>
          Drop another image or <u>browse</u>
        </span>
      ) : (
        <span>
          Drop images or <u>browse</u>
        </span>
      )
    }

    return (
      <div style={{ height, position: 'relative' }}>
        <Upload
          accept={{
            'image/*': [],
          }}
          title={title}
          invalid={!!get(errors, imagesFieldName)}
          multiple
          icon={UploadIcon}
          iconSize={20}
          maxSize={10485760}
          orientation="horizontal"
          onFileDrop={handleImageUpload}
          onFileDropRejected={handleDropRejection}
        />
      </div>
    )
  }

  return (
    <Subsection title={subsectionTitle}>
      <Loading isLoading={isLoading}>
        <S.Container>
          {renderImages()}
          <div>
            {renderUpload()}
            {get(errors, imagesFieldName) && (
              <InputUI.Error>{get(errors, imagesFieldName)}</InputUI.Error>
            )}
          </div>
          {renderOverlay()}
        </S.Container>
      </Loading>
    </Subsection>
  )
}
